/**
 * @description
 * Core authentication library implementing OAuth 2.0 flows for Google and GitHub.
 * Handles token generation, validation, session management, and secure state handling.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 * @see /admin/src/types/auth.ts
 */

import { AuthError } from '../types/auth';
import type { User } from '../types/auth';
import { isDevelopment } from './environment';
import { getDevUser } from './devAuth';


/**
 * Verify and decode JWT token
 * Note: In backend-driven OAuth, JWT verification is handled by the backend
 * This function is kept for compatibility but should not be used in production
 */
export async function verifyJWT(_token: string): Promise<never> {
  // In backend-driven OAuth, JWT verification should be done via API call to backend
  // This function is deprecated and kept only for compatibility
  throw new AuthError(
    'oauth_error',
    'JWT verification should be done via backend API. This function is deprecated.',
    { reason: 'backend_oauth_migration' }
  );
}

/**
 * Store authentication token in localStorage
 */
export function storeAuthToken(token: string): void {
  localStorage.setItem('auth_token', token);
}

/**
 * Get authentication token from localStorage
 */
export function getAuthToken(): string | null {
  // Check if in development mode
  if (isDevelopment()) {
    console.log('🔓 DEV MODE: Using mock authentication');
    return 'mock-dev-token-intars@byteable.ai';
  }

  // Production mode - use real token from localStorage or cookie
  const storedToken = localStorage.getItem('auth_token');
  if (storedToken) {
    return storedToken;
  }
  
  // Try to get token from cookie as fallback
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'session' || name === 'auth_token') {
      return value;
    }
  }
  
  return null;
}

/**
 * Remove authentication token from localStorage
 */
export function removeAuthToken(): void {
  localStorage.removeItem('auth_token');
}

/**
 * Get current user from stored token
 */
export async function getCurrentUser(): Promise<User | null> {
  // Check if in development mode
  if (isDevelopment()) {
    console.log('🔓 DEV MODE: Using mock authentication');
    return getDevUser();
  }

  // Production mode - verify JWT token via backend API
  // The JWT is stored in an HTTP-only cookie by the OAuth callback
  // We don't need to send it explicitly - the browser will send it automatically
  try {
    // Call backend API to verify token and get user info
    // The session cookie will be sent automatically by the browser
    const response = await fetch('/api/auth/me', {
      credentials: 'include', // Important: include cookies in the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      // If unauthorized, the session cookie is invalid or expired
      return null;
    }

    const data = await response.json() as { user: User };
    return data.user;
  } catch (error) {
    console.error('Failed to get current user:', error);
    return null;
  }
}

/**
 * Check if user is authenticated
 */
export async function isAuthenticated(): Promise<boolean> {
  const user = await getCurrentUser();
  return user !== null;
}

/**
 * Logout user by removing token
 */
export function logout(): void {
  removeAuthToken();
  sessionStorage.clear();
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */